import React from 'react';
import {FormattedMessage} from 'react-intl';
import WETDate from './WETDate';
import { Field, useField } from "formik";
import FadeLoader from "react-spinners/FadeLoader";

/**
 * labels must be pre-translated in originating component because this file's components use loops/callbacks to iterate over collections of items and you cannot use useIntl() inside
 * these loops.  Also, certain components may choose to dynamically show/hide content, and useIntl() cannot be included inside components that dynamically show/hide.
 * 
 * Recommendation is to perform all possible label translations for your component into an object at the top of the component and pass in translated label into these fields.
 */

export const DateFieldWithLabel = ({label, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-3"+(props.required ? " required" : "")}>{label} {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}</label>
		  <div className={meta && meta.touched && meta.error ? "col-md-4 has-error" : "col-md-4"}>
		    <WETDate disabled={props.disabled} className={meta && meta.touched && meta.error ? " has-error form-control full-width error" : "form-control full-width"} name={props.name} min={props.min} max={props.max} />
		  </div>
		  {meta && meta.touched && meta.error ? 
		  	<strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		  : null
		  }
		</div>
	);
};

export const TextFieldWithLabel = ({label, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-3"+(props.required ? " required" : "")}>
		    <abbr title={props.title}>{label}</abbr> {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		  </label>
		  <div className="col-md-4">
		    <Field disabled={props.disabled} readOnly={props.readonly} onPaste={props.onPaste} id={props.id} name={props.name} type="text" placeholder={props.placeholder} className={meta && meta.touched && meta.error ? " has-error form-control full-width error" : "form-control full-width"} maxLength={props.maxLength}/>
		  </div>
		  {meta && meta.touched && meta.error ? 
		    <strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		  : null
		  }
		</div>
	);
};

export const TextFieldWithLabelAndOnKeyDownHandler = ({label, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-3"+(props.required ? " required" : "")}>
		    {label} {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		  </label>
		  <div className="col-md-4">
		    <Field disabled={props.disabled} readOnly={props.readonly} onPaste={props.onPaste} onKeyDown={props.onKeyDown} id={props.id} name={props.name} type="text" placeholder={props.placeholder} className={meta && meta.touched && meta.error ? " has-error form-control full-width error" : "form-control full-width"} maxLength={props.maxLength}/>
		  </div>
		  {meta && meta.touched && meta.error ? 
		    <strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		  : null
		  }
		</div>
	);
};

export const TextFieldWithLabelAndCharCounter = ({label, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-3"+(props.required ? " required" : "")}>
		    {label} {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		  </label>
		  <div className="col-md-4">
		    <Field disabled={props.disabled} readOnly={props.readonly} onPaste={props.onPaste} id={props.id} name={props.name} type="text" placeholder={props.placeholder} className={meta && meta.touched && meta.error ? " has-error form-control full-width error" : "form-control full-width"} maxLength={props.maxLength}/>
		  </div>
		  <div className="col-md-5 text-right">
			<FormattedMessage id={props.charCountId} values={{count: props.charCount ? props.charCount.length : 0}} />
		  </div>
		  {meta && meta.touched && meta.error ? 
		    <strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		  : null
		  }
		</div>
	);
};

export const SelectFieldWithLabel = ({label, options, ...props}) => {
	const [field, meta, helpers] = useField(props);
	const handleChange = (e) => {
		if(props.onChange)
			props.onChange();
		helpers.setValue(e.target.value); // Update field value using Formik's helper 
		field.onChange(e); // Call Formik's default onChange
	};
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-3"+(props.required ? " required" : "")}>
		    {label} {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		  </label>
		  <div className={!!label ? "col-md-4" : "col-md-12 col-sm-12 col-xs-12"}>
		    <Field disabled={props.disabled} id={props.id} name={props.name} as="select" className={meta && meta.touched && meta.error ? " has-error form-control full-width error" : "form-control full-width"} onChange={handleChange}>
		      {options && options.map(function(option, index) {
		      	return (
		      		<option key={(this.id || this.name)+"-option-"+index} value={option.value}>{option.label || option.value}</option>
		      	);
		      }, props)}
		    </Field>
		  </div>
		  {meta && meta.touched && meta.error ? 
		  	<strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		  : null
		  }
		</div>
	);
}

export const SelectFieldWithLabelAndSpinner = ({label, options, ...props}) => {
	const [field, meta, helpers] = useField(props);
	const handleChange = (e) => {
		if(props.onChange)
			props.onChange();
		helpers.setValue(e.target.value); // Update field value using Formik's helper 
		field.onChange(e); // Call Formik's default onChange
	};
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-3"+(props.required ? " required" : "")}>
		    {label} {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		  </label>
		  <div className={!!label ? "col-md-4" : "col-md-12 col-sm-12 col-xs-12"}>
			{ options.length > 1 ? 
				<Field disabled={props.disabled} id={props.id} name={props.name} as="select" className={meta && meta.touched && meta.error ? " has-error form-control full-width error" : "form-control full-width"} onChange={handleChange}>
				{options && options.map(function(option, index) {
					return (
						<option key={(this.id || this.name)+"-option-"+index} value={option.value}>{option.label || option.value}</option>
					);
				}, props)}
				</Field>
			:
				<div id={props.id} name={props.name} className="full-width form-control" tabIndex="0" style={{ position: 'relative' }}>
					<span style={{ position:'absolute', marginLeft:'50%', marginRight:'50%', left:'0', right:'0' }}>
						<FadeLoader style={{ inset: '20px 0px 0px 0px', display: 'inherit', position: 'relative' }}
							color={"#888888"}
							loading={true}
							height={9}
							width={3}
							radius={2}
							margin={-7}
						/>
					</span>
				</div>
			}
		  </div>
		  {meta && meta.touched && meta.error ? 
		  	<strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		  : null
		  }
		</div>
	);
}

export const RadioButtonsWithLabel = ({label, options, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div id={props.id ? props.id : ''} className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.name || props.id} className={"control-label col-md-10"+(props.required ? " required" : "")}>
		    {!!label ? label : <></>} {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		    {meta && meta.touched && meta.error ? 
		    	<>&nbsp;<strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong></>
		    : null
		    }
		    <div className="checkbox gc-chckbxrdio" style={{marginLeft:20}}>
		      <ul className="list-unstyled lst-spcd-2">
		        {options && options.map(function(option, index) {
		        	return (
		        		<li key={(this.id || this.name)+"-"+index} className="radio">
		        		  <Field disabled={props.disabled} type="radio" name={this.name || this.id} id={(this.id || this.name)+"-"+index} value={option.value} />
		        		  <label htmlFor={(this.id || this.name)+"-"+index} className={option.label && option.required ? "required" : ""}>{option.label || option.value} {option.label && option.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}</label>
		        		</li>
		        	);
		        }, props)}
		      </ul>
		    </div>
		  </label>
		</div>
	);
}

export const ACERadioButtonsWithLabel = ({label, options, labelText1, labelText2, labelAnchorTag1, labelAnchorTag2, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div className="form-group col-md-12">
		  <label htmlFor={props.name || props.id} className={"control-label col-md-10"+(props.required ? " required" : "")}>
		    {!!labelText1 ? <FormattedMessage id={labelText1} values={{a: (chunks) => <a href={labelAnchorTag1}>{chunks}</a>}} /> : <></>} 
		    {!!labelText2 ? <FormattedMessage id={labelText2} values={{a: (chunks) => <a href={labelAnchorTag2}>{chunks}</a>}} /> : <></>} {props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
			{meta && meta.touched && meta.error ? 
		    	<strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		    : null
		    }
		    <div className="checkbox gc-chckbxrdio" style={{marginLeft:20}}>
		      <ul className="list-unstyled lst-spcd-2">
		        {options && options.map(function(option, index) {
		        	return (
		        		<li key={(this.id || this.name)+"-"+index} className="radio">
		        		  <Field disabled={props.disabled} type="radio" name={this.name || this.id} id={(this.id || this.name)+"-"+index} value={option.value} />
		        		  <label htmlFor={(this.id || this.name)+"-"+index} className={option.label && option.required ? "required" : ""}>{option.label || option.value} {option.label && option.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}</label>
		        		</li>
		        	);
		        }, props)}
		      </ul>
		    </div>
		  </label>
		</div>
	);
}

export const CheckboxWithLabel = ({label, options, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-10"+(label && props.required ? " required" : "")}>
		    {!!label ? <>{label}</> : <></>} {label && props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		    {meta && meta.touched && meta.error ? 
		    	<strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		    : null
		    }
		    <div className="checkbox gc-chckbxrdio" style={{marginLeft:20}}>
		      <ul className="list-unstyled lst-spcd-2">
		        { options && options.length ? 
		        	options.map(function(option, index) {
		        		return (
		        			<li key={(this.id || this.name)+"-"+index} className="checkbox">
		        			  <Field disabled={props.disabled} type="checkbox" name={this.name || this.id} id={(this.id || this.name)+"-"+index} value={option.value} />
		        			  <label htmlFor={(this.id || this.name)+"-"+index} className={option.label && option.required ? "required" : ""}>{option.label || option.value} {option.label && option.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}</label>
		        			</li>
		        		);
		        	}, props)
		        : null
		        }
		      </ul>
		    </div>
		  </label>
		</div>
	);
}

export const CheckboxWithLabelAndAnchorTag = ({label, options, ...props}) => {
	const meta = useField(props)[1];
	return (
		<div className="form-group col-md-12 magical-incantation">
		  <label htmlFor={props.id || props.name} className={"control-label col-md-10"+(label && props.required ? " required" : "")}>
		    {!!label ? <>{label}</> : <></>} {label && props.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}
		    {meta && meta.touched && meta.error ? 
		    	<strong className="error"><span className="label label-danger"><span className="prefix"><FormattedMessage id="globalAlert.error"/><FormattedMessage id="wet.colon"/> </span>{meta.error}</span></strong>
		    : null
		    }
		    <div className="checkbox gc-chckbxrdio" style={{marginLeft:20}}>
		      <ul className="list-unstyled lst-spcd-2">
		        { options && options.length ? 
		        	options.map(function(option, index) {
		        		return (
		        			<li key={(this.id || this.name)+"-"+index} className="checkbox">
		        			  <Field disabled={props.disabled} type="checkbox" name={this.name || this.id} id={(this.id || this.name)+"-"+index} value={option.value} />
		        			  <label htmlFor={(this.id || this.name)+"-"+index} className={option.label && option.required ? "required" : ""}>{option.label || option.value} {option.label && option.required ? <strong className="required"><FormattedMessage id="common.required.inBrackets"/></strong> : <></>}</label>
		        			  <div htmlFor={(this.id || this.name)+"-"+index}><a href={option.anchorTagHref} target="_blank" style={{padding: "0 0 0 20px"}} rel="noreferrer">{option.anchorTagDesc}</a> </div>
							  <div htmlFor={(this.id || this.name)+"-"+index} style={{padding: "0 0 0 20px", fontWeight: 400}}>{option.extraDesc}</div>
		        			</li>
		        		);
		        	}, props)
		        : null
		        }
		      </ul>
		    </div>
		  </label>
		</div>
	);
}